import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Envelope, MapPin, FacebookLogo, InstagramLogo, Clock, Ear, Monitor } from '@phosphor-icons/react';
import { MutuellesMarquee } from './components/MutuellesMarquee';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHITE_LOGO = '/api/static/branding/mrg-logo-white.png';

export const MrgFooter = ({ store, pages = [], categories = [] }) => {
  const navigate = useNavigate();
  const [cats, setCats] = useState(categories || []);
  const [pgs, setPgs] = useState(pages || []);
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/s/mrg/partners`).then(r => setPartners(r.data?.partners || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (categories && categories.length) { setCats(categories); return; }
    axios.get(`${API_URL}/api/s/mrg/categories`).then(r => setCats(r.data || [])).catch(() => {});
  }, [categories]);
  useEffect(() => {
    if (pages && pages.length) { setPgs(pages); return; }
    axios.get(`${API_URL}/api/s/mrg/pages`).then(r => setPgs(r.data || [])).catch(() => {});
  }, [pages]);

  if (!store) return null;
  const colors = store?.theme_config?.colors || { primary: '#111111', accent: '#FF004D' };
  const accent = colors.accent || '#FF004D';
  const bg = '#0e0f12';
  const id = store?.identity || {};
  const logoUrl = id.logo_url || WHITE_LOGO;
  const phone = id.contact_phone || '';
  const email = id.contact_email || '';
  const company = id.company_name || store?.name || 'MRG Optique & Audition';
  const address = id.address || 'Centre Médical, 44 Chemin du Bord de Crau';
  const city = `${id.postal_code || '13800'} ${id.city || 'Istres'}`;

  const findCat = (s) => (cats || []).find(c => (c.slug || '').includes(s));
  const soleil = findCat('soleil');
  const soleilGenders = soleil ? (cats || []).filter(c => c.parent_id === soleil.id) : [];
  const vue = findCat('vue');
  const lentille = findCat('lentille');
  const connect = findCat('connect');

  return (
    <footer className="text-white relative overflow-hidden" style={{ backgroundColor: '#0e0f12', fontFamily: "'Poppins', sans-serif" }} data-testid="mrg-footer">
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${accent}, #ff6aa0, ${accent})` }} />
      <MutuellesMarquee accent={accent} bg="#0e0f12" />

      {/* Partenaires / clubs & associations */}
      <div className="border-b border-white/10" data-testid="partners-band">
        <div className="max-w-[1320px] mx-auto px-5 pt-6 pb-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
            <span style={{ color: accent }} className="text-[12px] font-bold uppercase tracking-wider">Nos partenaires &amp; clubs soutenus</span>
          </div>
        </div>
        {partners.length > 0 ? (
          <div className="relative overflow-hidden ptn-wrap pb-6">
            <div className="flex items-center gap-2.5 w-max ptn-track py-0.5 px-4">
              {[...partners, ...partners, ...partners, ...partners].map((p, i) => {
                const inner = p.logo_url
                  ? <span className="bg-white rounded-md px-2 py-1 flex items-center justify-center h-9"><img src={p.logo_url} alt={p.name} className="max-h-7 max-w-[84px] object-contain" loading="lazy" /></span>
                  : <span className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/75 text-[12px] font-medium whitespace-nowrap">{p.name}</span>;
                return p.link
                  ? <a key={i} href={p.link} target="_blank" rel="noopener noreferrer" title={p.name} className="shrink-0 hover:opacity-100 opacity-90 transition-opacity">{inner}</a>
                  : <span key={i} title={p.name} className="shrink-0">{inner}</span>;
              })}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 md:w-40 z-10" style={{ background: `linear-gradient(to right, ${bg} 0%, ${bg}f0 35%, ${bg}00 100%)` }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-40 z-10" style={{ background: `linear-gradient(to left, ${bg} 0%, ${bg}f0 35%, ${bg}00 100%)` }} />
            <style>{`
              @keyframes ptnScroll { from { transform: translateX(0); } to { transform: translateX(-25%); } }
              .ptn-track { animation: ptnScroll 50s linear infinite; }
              .ptn-wrap:hover .ptn-track { animation-play-state: paused; }
            `}</style>
          </div>
        ) : (
          <div className="max-w-[1320px] mx-auto px-5 pb-6">
            <p className="text-white/45 text-[13px]">
              MRG soutient des <strong className="text-white/70">clubs et associations</strong> locaux et collabore avec des partenaires.{' '}
              <Link to="/page/contact" className="font-semibold hover:underline" style={{ color: accent }}>Devenir partenaire</Link>.
            </p>
          </div>
        )}
      </div>
      <div className="max-w-[1320px] mx-auto px-5 py-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
        {/* Brand block */}
        <div className="col-span-2">
          <img src={WHITE_LOGO} alt={company} className="h-12 w-auto object-contain mb-5" onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-white/55 text-[14px] max-w-xs leading-relaxed">
            Votre opticien à Istres. Les plus grandes marques de lunettes de soleil et de vue, et lentilles de contact.
          </p>
          <div className="mt-5 space-y-2.5 text-[14px]">
            <div className="flex items-start gap-2.5 text-white/70"><MapPin size={17} weight="fill" style={{ color: accent }} className="mt-0.5 shrink-0" /><span>{address}, {city}</span></div>
            {phone && <a href={`tel:${phone.replace(/\s/g,'')}`} className="flex items-center gap-2.5 text-white/70 hover:text-white"><Phone size={16} weight="fill" style={{ color: accent }} /> {phone}</a>}
            {email && <a href={`mailto:${email}`} className="flex items-center gap-2.5 text-white/70 hover:text-white"><Envelope size={16} weight="fill" style={{ color: accent }} /> {email}</a>}
            <div className="flex items-center gap-2.5 text-white/70"><Clock size={16} weight="fill" style={{ color: accent }} /> Lun–Sam : 9h–13h / 14h–19h</div>
          </div>
          {/* Audio note */}
          <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full" style={{ backgroundColor: accent + '1A', border: `1px solid ${accent}40` }}>
            <Ear size={16} weight="fill" style={{ color: accent }} />
            <span className="text-[12.5px] text-white/85"><strong>Audioprothèse</strong> — prochainement disponible</span>
          </div>
          <div className="flex gap-3 mt-5">
            {id.social_facebook && <a href={id.social_facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><FacebookLogo size={18} weight="fill" /></a>}
            {id.social_instagram && <a href={id.social_instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><InstagramLogo size={18} weight="fill" /></a>}
          </div>
        </div>

        {/* Lunettes de soleil */}
        <div>
          <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4" style={{ color: accent }}>Lunettes de soleil</h4>
          <ul className="space-y-2.5 text-[14px]">
            {soleilGenders.length > 0 ? soleilGenders.map(g => (
              <li key={g.id}><button onClick={() => navigate(`/?cat=${g.id}`)} className="text-white/65 hover:text-white transition-colors text-left">{g.name}</button></li>
            )) : (soleil && <li><button onClick={() => navigate(`/?cat=${soleil.id}`)} className="text-white/65 hover:text-white text-left">Lunettes de soleil</button></li>)}
          </ul>
        </div>

        {/* Optique & lentilles */}
        <div>
          <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4" style={{ color: accent }}>Optique & lentilles</h4>
          <ul className="space-y-2.5 text-[14px]">
            {vue && <li><button onClick={() => navigate(`/?cat=${vue.id}`)} className="text-white/65 hover:text-white text-left">Lunettes de vue</button></li>}
            {lentille && <li><button onClick={() => navigate(`/?cat=${lentille.id}`)} className="text-white/65 hover:text-white text-left">Lentilles de contact</button></li>}
            {connect && <li><button onClick={() => navigate(`/?cat=${connect.id}`)} className="text-white/65 hover:text-white text-left">Lunettes connectées</button></li>}
            <li><Link to="/marques" className="text-white/65 hover:text-white">Toutes les marques</Link></li>
          </ul>
        </div>

        {/* Infos */}
        <div>
          <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4" style={{ color: accent }}>Informations</h4>
          <ul className="space-y-2.5 text-[14px]">
            <li><Link to="/blog" className="text-white/65 hover:text-white">Nos conseils</Link></li>
            <li><Link to="/page/contact" className="text-white/65 hover:text-white">Nous contacter</Link></li>
            {(pgs || []).slice(0, 4).map(p => (
              <li key={p.id || p.slug}><Link to={`/page/${p.slug}`} className="text-white/65 hover:text-white">{p.title}</Link></li>
            ))}
          </ul>
        </div>
      </div>

      {/* SEO local links (ville par ville) */}
      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-5 py-7" data-testid="footer-local-seo">
          <h4 className="text-[12px] font-semibold uppercase tracking-[0.18em] mb-4 text-white/50">Votre opticien & audioprothésiste près de chez vous</h4>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
            <Link to="/opticien-istres" className="text-white/55 hover:text-white transition-colors">Opticien Istres</Link>
            <span className="text-white/20">·</span>
            <Link to="/opticien-fos-sur-mer" className="text-white/55 hover:text-white transition-colors">Opticien Fos-sur-Mer</Link>
            <span className="text-white/20">·</span>
            <Link to="/opticien-miramas" className="text-white/55 hover:text-white transition-colors">Opticien Miramas</Link>
            <span className="text-white/20">·</span>
            <Link to="/audioprothesiste-istres" className="text-white/55 hover:text-white transition-colors">Audioprothésiste Istres</Link>
            <span className="text-white/20">·</span>
            <Link to="/audioprothesiste-fos-sur-mer" className="text-white/55 hover:text-white transition-colors">Audioprothésiste Fos-sur-Mer</Link>
            <span className="text-white/20">·</span>
            <Link to="/lunettes-de-soleil-istres" className="text-white/55 hover:text-white transition-colors">Lunettes de soleil Istres</Link>
            <span className="text-white/20">·</span>
            <Link to="/lentilles-de-contact-istres" className="text-white/55 hover:text-white transition-colors">Lentilles de contact Istres</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-5 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/45 text-[12.5px]">© {new Date().getFullYear()} {company} — Tous droits réservés</p>
          <div className="flex items-center gap-4">
            <Link to="/kiosque" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12.5px] font-medium" data-testid="footer-kiosk">
              <Monitor size={15} weight="fill" style={{ color: accent }} /> Mode borne tactile
            </Link>
            <div className="flex items-center gap-2">
              {['CB', 'Visa', 'Mastercard', 'PayPal'].map(m => (
                <span key={m} className="px-2.5 py-1 rounded bg-white/10 text-white/70 text-[11px] font-medium">{m}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Agency credit */}
      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-5 py-4 flex items-center justify-center gap-2 text-white/40 text-[12px] flex-wrap text-center">
          <span>Site &amp; application développés par</span>
          <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-100 opacity-85 transition-opacity" data-testid="msc-credit">
            <img src="/api/static/branding/msc-logo.png" alt="Agence MSC" className="h-6 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            <span className="font-semibold text-white/70">l&apos;agence MSC</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default MrgFooter;
